import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, PencilSimple, Trash, MagnifyingGlass, Phone, Envelope, CalendarBlank, Warning, Users } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { EmptyState } from "../components/EmptyState";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError, formatDate } from "../lib/utils-extra";

const FREQ = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "semi_annual", label: "Semi-Annual" },
  { value: "annual", label: "Annual" },
];

const empty = {
  full_name: "", position_id: "", department: "", supervisor_id: null,
  branch_id: null, team_id: null, work_schedule_id: null,
  date_hired: "", employment_status: "Active", email: "", phone: "",
  evaluation_frequency: "quarterly", next_evaluation_date: "", active: true,
};

const NONE = "__none__";

export default function Employees() {
  const [list, setList] = useState([]);
  const [positions, setPositions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);

  const reload = useCallback(() => {
    Promise.all([
      api.get("/employees"), api.get("/positions"),
      api.get("/branches"), api.get("/teams"), api.get("/work-schedules"),
    ]).then(([e, p, b, t, s]) => {
      setList(e.data);
      setPositions(p.data);
      setBranches(b.data || []);
      setTeams(t.data || []);
      setSchedules(s.data || []);
    });
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (emp) => {
    setEditId(emp.id);
    setForm({ ...empty, ...emp, position_id: emp.position_id || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.full_name?.trim()) return toast.error("Name is required");
    try {
      const payload = { ...form };
      if (!payload.position_id) payload.position_id = null;
      if (!payload.supervisor_id) payload.supervisor_id = null;
      if (!payload.next_evaluation_date) payload.next_evaluation_date = null;
      if (!payload.date_hired) payload.date_hired = null;
      if (!payload.email) payload.email = null;
      // Auto-fill department from position
      if (payload.position_id && !payload.department) {
        const pos = positions.find((p) => p.id === payload.position_id);
        if (pos) payload.department = pos.department;
      }
      if (editId) await api.put(`/employees/${editId}`, payload);
      else await api.post("/employees", payload);
      toast.success("Employee saved");
      setOpen(false);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!confirm("Archive this employee?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Archived");
    reload();
  };

  const filtered = list.filter((e) =>
    [e.full_name, e.email, e.position_name, e.department]
      .filter(Boolean).join(" ").toLowerCase().includes(search.toLowerCase())
  );

  const noPositions = positions.length === 0;

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Roster</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Employees</h1>
          <p className="text-[#475569] text-sm mt-1">Everyone on your team.</p>
        </div>
        <Button data-testid="new-employee-button" onClick={openCreate} disabled={noPositions}
                className="bg-[#0EA5E9] hover:bg-[#0284C7]">
          <Plus size={18} className="mr-1" /> New Employee
        </Button>
      </div>

      {noPositions && (
        <Card className="border-[#D97706]/30 bg-[#D97706]/5" data-testid="employees-dependency-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <Warning size={20} weight="duotone" color="#D97706" className="mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-[#0F172A]">Create a position first</div>
              <p className="text-[#475569] mt-0.5">
                Employees must be assigned a role. Add at least one position before creating employees.
              </p>
            </div>
            <Link to="/settings?tab=positions">
              <Button size="sm" variant="outline" data-testid="goto-positions-button">Go to Positions</Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {list.length > 0 && (
      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
        <Input className="pl-10 h-11" placeholder="Search employees…" value={search}
               onChange={(e) => setSearch(e.target.value)} data-testid="employee-search-input" />
      </div>
      )}

      {list.length === 0 ? (
        <EmptyState
          testId="employees-empty-state"
          icon={Users}
          title="No employees yet"
          description="You need employees before you can run evaluations. Add your first team member to get started."
          actions={
            <Button data-testid="empty-create-employee-button" onClick={openCreate} disabled={noPositions}
                    className="bg-[#0EA5E9] hover:bg-[#0284C7]">
              <Plus size={18} className="mr-1" /> Create First Employee
            </Button>
          }
        />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((emp) => (
          <Card key={emp.id} className="surface-card border-[#E2E8F0]" data-testid={`employee-card-${emp.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] flex items-center justify-center text-white font-display font-semibold">
                  {emp.full_name?.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base font-semibold truncate">{emp.full_name}</div>
                  <div className="text-xs text-[#475569] truncate">{emp.position_name || "—"} · {emp.department || "—"}</div>
                </div>
                <Badge className={emp.active ? "bg-[#16A34A]/10 text-[#16A34A] border-0" : "bg-[#F1F5F9] text-[#475569] border-0"}>
                  {emp.active ? emp.employment_status : "Inactive"}
                </Badge>
              </div>
              <div className="mt-4 space-y-2 text-sm text-[#475569]">
                {emp.email && <div className="flex items-center gap-2"><Envelope size={14} /><span className="truncate">{emp.email}</span></div>}
                {emp.phone && <div className="flex items-center gap-2"><Phone size={14} />{emp.phone}</div>}
                <div className="flex items-center gap-2"><CalendarBlank size={14} /> Next review: <strong className="text-[#0F172A]">{formatDate(emp.next_evaluation_date)}</strong></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Link to={`/employees/${emp.id}`}>
                  <Button variant="default" size="sm" className="bg-[#0EA5E9] hover:bg-[#0284C7]" data-testid={`open-employee-${emp.id}`}>
                    View History
                  </Button>
                </Link>
                <Button variant="outline" size="sm" onClick={() => openEdit(emp)} data-testid={`edit-employee-${emp.id}`}>
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(emp.id)} data-testid={`archive-employee-${emp.id}`}>
                  <Trash size={14} className="mr-1 text-[#DC2626]" /> Archive
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit employee" : "New employee"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Label className="small-label">Full Name</Label>
              <Input data-testid="employee-name-input" className="mt-2" value={form.full_name}
                     onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Position</Label>
              <Select value={form.position_id || ""} onValueChange={(v) => {
                const pos = positions.find((p) => p.id === v);
                setForm({ ...form, position_id: v, department: pos?.department || form.department });
              }}>
                <SelectTrigger className="mt-2" data-testid="employee-position-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Department</Label>
              <Input className="mt-2" value={form.department}
                     onChange={(e) => setForm({ ...form, department: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Branch</Label>
              <Select value={form.branch_id || NONE} onValueChange={(v) => setForm({ ...form, branch_id: v === NONE ? null : v })}>
                <SelectTrigger className="mt-2" data-testid="employee-branch-select"><SelectValue placeholder="No branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No branch</SelectItem>
                  {branches.filter((b) => b.active !== false).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Team</Label>
              <Select value={form.team_id || NONE} onValueChange={(v) => setForm({ ...form, team_id: v === NONE ? null : v })}>
                <SelectTrigger className="mt-2" data-testid="employee-team-select"><SelectValue placeholder="No team" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No team</SelectItem>
                  {teams.filter((t) => t.active !== false).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Work Schedule</Label>
              <Select value={form.work_schedule_id || NONE} onValueChange={(v) => setForm({ ...form, work_schedule_id: v === NONE ? null : v })}>
                <SelectTrigger className="mt-2" data-testid="employee-schedule-select"><SelectValue placeholder="No schedule" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>No schedule</SelectItem>
                  {schedules.filter((s) => s.active !== false).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Email</Label>
              <Input type="email" className="mt-2" value={form.email || ""}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Phone</Label>
              <Input className="mt-2" value={form.phone || ""}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Date Hired</Label>
              <Input type="date" className="mt-2" value={form.date_hired || ""}
                     onChange={(e) => setForm({ ...form, date_hired: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Evaluation Frequency</Label>
              <Select value={form.evaluation_frequency} onValueChange={(v) => setForm({ ...form, evaluation_frequency: v })}>
                <SelectTrigger className="mt-2" data-testid="employee-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Next Evaluation Date</Label>
              <Input type="date" className="mt-2" value={form.next_evaluation_date || ""}
                     onChange={(e) => setForm({ ...form, next_evaluation_date: e.target.value })} />
            </div>
            <div>
              <Label className="small-label">Employment Status</Label>
              <Input className="mt-2" value={form.employment_status}
                     onChange={(e) => setForm({ ...form, employment_status: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-employee-button" onClick={save} className="bg-[#0EA5E9] hover:bg-[#0284C7]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
